'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

// Helper function to render text with [bold] syntax
const renderTextWithBold = (text: string) => {
  const parts = text.split(/(\[bold][^[]*?\[bold])/g);

  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/\[bold]([^[]*?)\[bold]/);
        if (match) {
          return (
            <strong key={i} className="font-bold text-[18px] leading-[1.2]">
              {match[1]}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

// Helper function to render text with bulletpoints
const renderTextWithBulletpoints = (text: string) => {
  // Check if text contains [Bulletpoint] keyword
  if (text.includes('[Bulletpoint]')) {
    // Split by [Bulletpoint] to separate intro text and bullet items
    const parts = text.split('[Bulletpoint]');
    const introText = parts[0].trim(); // First part is the intro paragraph
    const bulletItems = parts.slice(1).filter((part) => part.trim()); // Rest are bullets

    return (
      <>
        {/* Render intro paragraph if exists */}
        {introText && (
          <p className="text-[18px] text-white leading-[1.5] font-normal font-inter mb-4">
            {renderTextWithBold(introText)}
          </p>
        )}

        {/* Render bullet list */}
        {bulletItems.length > 0 && (
          <ul className="list-disc pl-0 space-y-3">
            {bulletItems.map((part, index) => {
              const trimmedPart = part.trim();
              return (
                <li
                  key={index}
                  className="text-[18px] leading-[1.5] font-normal font-inter ml-[27px]"
                >
                  {renderTextWithBold(trimmedPart)}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  // If no bulletpoints, just return the text
  return (
    <p className="text-[18px] text-white leading-[1.5] font-normal font-inter">
      {text}
    </p>
  );
};

/**
 * Props for `ProjectCaseStudy`.
 */
export type ProjectCaseStudyProps =
  SliceComponentProps<Content.ProjectCaseStudySlice>;

/**
 * Component for "ProjectCaseStudy" Slices.
 */
const ProjectCaseStudy: FC<ProjectCaseStudyProps> = ({ slice }) => {
  const {
    title,
    challenge_heading,
    challenge_body,
    solution_heading,
    solution_body,
    results_gradient_dark_to_light,
    results_heading,
    results_intro,
    results_highlights,
    black_to_green_gradient,
  } = slice.primary;

  const titleRef = useRef<HTMLDivElement>(null);
  const challengeRef = useRef<HTMLDivElement>(null);
  const solutionRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-right');
          entry.target.classList.remove('opacity-0', 'translate-x-12');
        }
      });
    }, observerOptions);

    const elements = [titleRef, challengeRef, solutionRef, resultsRef];
    const currentElements: (HTMLDivElement | null)[] = [];

    elements.forEach((ref, index) => {
      if (ref.current) {
        const element = ref.current;
        currentElements.push(element);
        element.classList.add(
          'opacity-0',
          'translate-x-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        element.style.transitionDelay = `${index * 150}ms`;
        observer.observe(element);
      }
    });

    return () => {
      currentElements.forEach((element) => {
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  // Determine background gradient based on toggle
  const backgroundStyle = black_to_green_gradient
    ? {
        background: 'linear-gradient(180deg, #0E1110 0%, #0F3932 53.29%)',
      }
    : {
        background: 'linear-gradient(180deg, #0F3932 0%, #0E1110 53.29%)',
      };

  // Determine results section gradient based on toggle
  const resultsBackgroundStyle = results_gradient_dark_to_light
    ? {
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0) 100%)',
      }
    : {
        background:
          'linear-gradient(180deg, rgba(2, 104, 70, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)',
      };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-16 lg:py-24"
      style={backgroundStyle}
    >
      <div className="container mx-auto px-6 lg:px-[200px]">
        <div className="flex flex-col gap-12 md:gap-14 lg:gap-[60px]">
          {/* Title */}
          {title && (
            <div ref={titleRef}>
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-[40px] font-medium text-white leading-[1.2] font-inter">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
          )}

          {/* Challenge and Solution Section */}
          <div className="flex flex-col gap-6 lg:gap-9">
            {/* Challenge */}
            <div
              ref={challengeRef}
              className="flex flex-col gap-3 lg:gap-[12px]"
            >
              {challenge_heading && (
                <div>
                  <PrismicRichText
                    field={challenge_heading}
                    components={{
                      heading3: ({ children }) => (
                        <h3 className="text-2xl md:text-[28px] font-medium text-white leading-[1.2] font-inter">
                          {children}
                        </h3>
                      ),
                    }}
                  />
                </div>
              )}

              {challenge_body && (
                <div className="text-white">
                  <PrismicRichText
                    field={challenge_body}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-[18px] text-white leading-[1.5] font-normal font-inter mb-4 last:mb-0">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-bold">{children}</strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Solution */}
            <div
              ref={solutionRef}
              className="flex flex-col gap-3 lg:gap-[12px]"
            >
              {solution_heading && (
                <div>
                  <PrismicRichText
                    field={solution_heading}
                    components={{
                      heading3: ({ children }) => (
                        <h3 className="text-2xl md:text-[28px] font-medium text-white leading-[1.2] font-inter">
                          {children}
                        </h3>
                      ),
                    }}
                  />
                </div>
              )}

              {solution_body && (
                <div className="text-white">
                  {renderTextWithBulletpoints(asText(solution_body) || '')}
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div
            ref={resultsRef}
            className="rounded-2xl p-6 lg:p-[24px] shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)]"
            style={resultsBackgroundStyle}
          >
            <div className="flex flex-col gap-4 lg:gap-6">
              {/* Results Header */}
              <div className="flex flex-col gap-3 lg:gap-[12px]">
                {results_heading && (
                  <div>
                    <PrismicRichText
                      field={results_heading}
                      components={{
                        heading3: ({ children }) => (
                          <h3 className="text-2xl md:text-[28px] font-medium text-white leading-[1.2] font-inter">
                            {children}
                          </h3>
                        ),
                      }}
                    />
                  </div>
                )}

                {results_intro && (
                  <div>
                    <PrismicRichText
                      field={results_intro}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="text-[18px] text-white leading-[1.5] font-normal font-inter">
                            {children}
                          </p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic">{children}</em>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Results Highlights */}
              {results_highlights && results_highlights.length > 0 && (
                <div className="flex flex-col gap-2 lg:gap-[8px]">
                  {results_highlights.map((item, index) => {
                    // Determine highlight background based on results gradient toggle
                    const highlightBackground = results_gradient_dark_to_light
                      ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)'
                      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)';

                    return (
                      <div
                        key={index}
                        className="rounded-3xl px-6 py-4 lg:px-[24px] lg:py-[16px]"
                        style={{
                          background: highlightBackground,
                        }}
                      >
                        {item.highlight && (
                          <div className="text-white text-[14px] md:text-[18px] leading-[1.5] font-normal font-inter">
                            {renderTextWithBold(asText(item.highlight) || '')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectCaseStudy;
